import React from "react";
import { useTranslation } from "react-i18next";

import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import { asTitle } from "utils/Utils";

export type BlueprintActionbarProps = {
	copyEnabled: boolean; // Only if one selected and user is admin
	editEnabled: boolean; // Only if one selected and user is admin
	infoEnabled: boolean; // Only if one selected and user has read
	deleteEnabled: boolean; // If at least one selected and user is admin of all

	onRefresh: () => void;
	onNew: () => void;
	onCopy: () => void;
	onEdit: () => void;
	onShowInfo: () => void;
	onDelete: () => void;
};

const BlueprintActionbar: React.FC<BlueprintActionbarProps> = (
	props: BlueprintActionbarProps
) => {
	const { t } = useTranslation();

	return (
		<Grid
			container
			alignItems="center"
			sx={{
				width: "fit-content",
				border: 1,
				borderColor: "divider",
				borderRadius: 1,
				backgroundColor: "background.paper",
				"& hr": {
					my: 0,
					mx: 0.5
				}
			}}>
			<Tooltip title={asTitle(t("refresh"))}>
				<IconButton
					aria-label="refresh"
					onClick={props.onRefresh}
					size="large">
					<RefreshOutlinedIcon />
				</IconButton>
			</Tooltip>
			<Divider orientation="vertical" flexItem />
			<Tooltip title={asTitle(t("new"))}>
				<IconButton aria-label="new" onClick={props.onNew} size="large">
					<InsertDriveFileIcon />
				</IconButton>
			</Tooltip>
			<Divider orientation="vertical" flexItem />
			<Tooltip title={asTitle(t("copy"))}>
				<span>
					<IconButton
						aria-label="copy"
						disabled={!props.copyEnabled}
						onClick={props.onCopy}
						size="large">
						<FileCopyOutlinedIcon />
					</IconButton>
				</span>
			</Tooltip>
			<Tooltip title={asTitle(t("edit"))}>
				<span>
					<IconButton
						aria-label="edit"
						disabled={!props.editEnabled}
						onClick={props.onEdit}
						size="large">
						<EditOutlinedIcon />
					</IconButton>
				</span>
			</Tooltip>
			<Tooltip title={asTitle(t("info"))}>
				<span>
					<IconButton
						aria-label="info"
						disabled={!props.infoEnabled}
						onClick={props.onShowInfo}
						size="large">
						<InfoOutlinedIcon />
					</IconButton>
				</span>
			</Tooltip>
			<Divider orientation="vertical" flexItem />
			<Tooltip title={asTitle(t("delete"))}>
				<span>
					<IconButton
						aria-label="delete"
						disabled={!props.deleteEnabled}
						onClick={props.onDelete}
						size="large">
						<DeleteOutlinedIcon />
					</IconButton>
				</span>
			</Tooltip>
		</Grid>
	);
};

export default BlueprintActionbar;
